/**
 * "New options" panel: values in the file that the team's playbook does not
 * have yet (e.g. a formation called "L Strong"). One compact list; each value
 * is added as a new option by default, can be matched to an existing option or
 * ignored. Used on both the new-game and existing-game paths.
 */
import { useEffect, useMemo, useState } from 'react';

import { useApiClient } from '../../app/providers';
import type {
  HudlUnknownValue,
  HudlValueAction,
  HudlValueDecision,
  StatCategory,
  StatOption,
} from '../../lib/api-types';

/** Play field → playbook stat whose options it uses. */
const FIELD_CATEGORY: Record<string, StatCategory> = {
  offensive_formation: 'FORMATION',
  defensive_front: 'FRONT',
  defensive_coverage: 'COVERAGE',
  defensive_blitz: 'BLITZ',
  opponent_blitz: 'BLITZ',
  offensive_personnel: 'PERSONNEL',
  offensive_concept: 'OFF_CONCEPT',
  defensive_concept: 'DEF_CONCEPT',
} as Record<string, StatCategory>;

export function valueKey(value: { field: string; value: string }): string {
  return `${value.field}\u0000${value.value}`;
}

/**
 * The server lists an unknown value once per side (our formation "Strong" and
 * the opponent's "Strong" are separate option lists), but a decision is keyed
 * by field + value and applies to both. Show each value once, counts summed.
 */
export function mergeUnknownValues(values: readonly HudlUnknownValue[]): HudlUnknownValue[] {
  const merged = new Map<string, HudlUnknownValue>();
  for (const value of values) {
    const key = valueKey(value);
    const existing = merged.get(key);
    if (existing === undefined) {
      merged.set(key, { ...value });
    } else {
      existing.count += value.count;
      if (existing.suggestion === null) existing.suggestion = value.suggestion;
    }
  }
  return [...merged.values()].sort(
    (left, right) => left.label.localeCompare(right.label) || right.count - left.count
  );
}

/** Default choice for every unknown value: add it (or use the suggestion when there is one). */
export function defaultValueDecisions(
  values: readonly HudlUnknownValue[]
): Map<string, HudlValueDecision> {
  return new Map(
    mergeUnknownValues(values).map((value) => [
      valueKey(value),
      value.suggestion === null
        ? { field: value.field, value: value.value, action: 'create' as const }
        : {
            field: value.field,
            value: value.value,
            action: 'map' as const,
            map_to: value.suggestion.value,
          },
    ])
  );
}

export interface NewOptionsPanelProps {
  values: readonly HudlUnknownValue[];
  decisions: Map<string, HudlValueDecision>;
  onChange(next: Map<string, HudlValueDecision>): void;
}

export function NewOptionsPanel({
  values: rawValues,
  decisions,
  onChange,
}: NewOptionsPanelProps): JSX.Element | null {
  const values = useMemo(() => mergeUnknownValues(rawValues), [rawValues]);
  const api = useApiClient();
  const [library, setLibrary] = useState<StatOption[]>([]);

  useEffect(() => {
    if (values.length === 0) return undefined;
    const controller = new AbortController();
    api
      .getPlaybookLibrary(controller.signal)
      .then((payload) => setLibrary(payload.items))
      .catch(() => {
        /* Matching falls back to the server's suggestion only. */
      });
    return () => controller.abort();
  }, [api, values.length]);

  const optionsByField = useMemo(() => {
    const result = new Map<string, { value: string; label: string }[]>();
    for (const value of values) {
      if (result.has(value.field)) continue;
      const category = FIELD_CATEGORY[value.field];
      const seen = new Set<string>();
      const list: { value: string; label: string }[] = [];
      for (const option of library) {
        if (option.category !== category || !option.is_enabled || seen.has(option.value)) continue;
        seen.add(option.value);
        list.push({ value: option.value, label: option.label });
      }
      if (value.suggestion !== null && !seen.has(value.suggestion.value)) {
        list.unshift(value.suggestion);
      }
      list.sort((left, right) => left.label.localeCompare(right.label));
      result.set(value.field, list);
    }
    return result;
  }, [library, values]);

  if (values.length === 0) return null;

  const groups = new Map<string, HudlUnknownValue[]>();
  for (const value of values) {
    const list = groups.get(value.label) ?? [];
    list.push(value);
    groups.set(value.label, list);
  }

  function set(value: HudlUnknownValue, action: HudlValueAction, mapTo?: string): void {
    const next = new Map(decisions);
    next.set(valueKey(value), {
      field: value.field,
      value: value.value,
      action,
      ...(action === 'map' ? { map_to: mapTo ?? value.suggestion?.value ?? '' } : {}),
    });
    onChange(next);
  }

  return (
    <section
      className="hudl-newopts"
      aria-labelledby="hudl-newopts-title"
      data-testid="hudl-new-options"
    >
      <div className="hudl-newopts__head">
        <h3 id="hudl-newopts-title">New options</h3>
        <p className="muted">
          These values are not in your playbook yet. Add them, match them to an option you already
          have, or leave them out.
        </p>
      </div>
      {[...groups.entries()].map(([label, list]) => (
        <div key={label} className="hudl-newopts__group">
          <h4 className="hudl-newopts__field">{label}</h4>
          <ul className="hudl-newopts__list">
            {list.map((value) => {
              const decision = decisions.get(valueKey(value));
              const action = decision?.action ?? 'create';
              const choices = optionsByField.get(value.field) ?? [];
              return (
                <li key={valueKey(value)} className="hudl-newopt" data-value={value.value}>
                  <div className="hudl-newopt__value">
                    <strong>{value.value}</strong>
                    <span className="muted">
                      {value.count} {value.count === 1 ? 'play' : 'plays'}
                    </span>
                  </div>
                  <div className="hudl-newopt__actions">
                    <div
                      className="hudl-mini-seg"
                      role="group"
                      aria-label={`What to do with ${value.value}`}
                    >
                      <button
                        type="button"
                        aria-pressed={action === 'create'}
                        onClick={() => set(value, 'create')}
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        aria-pressed={action === 'map'}
                        onClick={() => set(value, 'map', choices[0]?.value)}
                        disabled={choices.length === 0}
                        title={
                          choices.length === 0
                            ? `Your playbook has no ${label.toLowerCase()} options to match yet`
                            : undefined
                        }
                      >
                        Match
                      </button>
                      <button
                        type="button"
                        aria-pressed={action === 'ignore'}
                        onClick={() => set(value, 'ignore')}
                      >
                        Leave out
                      </button>
                    </div>
                    {action === 'map' ? (
                      <select
                        className="field__control hudl-newopt__map"
                        value={decision?.map_to ?? ''}
                        onChange={(changeEvent) => set(value, 'map', changeEvent.target.value)}
                        aria-label={`Match ${value.value} to`}
                      >
                        {choices.map((choice) => (
                          <option key={choice.value} value={choice.value}>
                            {choice.label}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}
